/* ==========================================================
   Aneka Cart Enhancements
   - Gift wrap add / remove toggle
   - Recommendation "Add" buttons (Liquid-rendered cards)
   - Free-shipping card auto-refresh on cart changes
   ========================================================== */

document.addEventListener('DOMContentLoaded', () => {
  initGiftWrap();
  initRecoButtons();
  initRecoCarousel();
  initCartUpdateListener();
});

/* ----------------------------------------------------------
   Helpers
   ---------------------------------------------------------- */

/**
 * Returns the qualified Shopify section IDs that Dawn uses for cart
 * section re-renders (read from data-id attributes Dawn writes).
 */
function getCartSectionIds() {
  const ids = [];
  const cartItemsEl  = document.getElementById('main-cart-items');
  const cartFooterEl = document.getElementById('main-cart-footer');
  if (cartItemsEl?.dataset?.id)  ids.push(cartItemsEl.dataset.id);
  if (cartFooterEl?.dataset?.id) ids.push(cartFooterEl.dataset.id);
  ids.push('cart-icon-bubble');
  return ids;
}

/**
 * Swaps re-rendered section HTML for the Dawn cart sections
 * (cart item rows, cart footer totals, icon badge) from a
 * parsedState.sections-style object.
 * Uses HTMLUpdateUtility.setInnerHTML so inline <script> tags
 * are re-executed (needed for gift-wrap observer IIFE).
 */
function applyCartSections(sectionsHtml) {
  const parser = new DOMParser();

  // --- main-cart-items  (.js-contents) ---
  const cartItemsEl = document.getElementById('main-cart-items');
  const cartItemsId = cartItemsEl?.dataset?.id;
  if (cartItemsEl && cartItemsId && sectionsHtml[cartItemsId]) {
    const doc = parser.parseFromString(sectionsHtml[cartItemsId], 'text/html');
    const newContents = doc.querySelector('.js-contents');
    const oldContents = cartItemsEl.querySelector('.js-contents') || cartItemsEl;
    if (newContents && oldContents) {
      HTMLUpdateUtility.setInnerHTML(oldContents, newContents.innerHTML);
    }
  }

  // --- main-cart-footer (.js-contents) ---
  const cartFooterEl = document.getElementById('main-cart-footer');
  const cartFooterId = cartFooterEl?.dataset?.id;
  if (cartFooterEl && cartFooterId && sectionsHtml[cartFooterId]) {
    const doc = parser.parseFromString(sectionsHtml[cartFooterId], 'text/html');
    const newContents = doc.querySelector('.js-contents');
    const oldContents = cartFooterEl.querySelector('.js-contents') || cartFooterEl;
    if (newContents && oldContents) {
      HTMLUpdateUtility.setInnerHTML(oldContents, newContents.innerHTML);
    }
  }

  // --- cart icon bubble ---
  const bubbleHtml = sectionsHtml['cart-icon-bubble'];
  if (bubbleHtml) {
    const bubbleEl = document.getElementById('cart-icon-bubble');
    if (bubbleEl) {
      const doc = parser.parseFromString(bubbleHtml, 'text/html');
      const newBubble = doc.querySelector('.shopify-section');
      if (newBubble) bubbleEl.innerHTML = newBubble.innerHTML;
    }
  }
}

/* ----------------------------------------------------------
   Cart update pubsub is now owned by aneka-tier-discount.js,
   which subscribes to PUB_SUB_EVENTS.cartUpdate itself and
   recomputes the tier tracker (bar + message + recommendations)
   straight from /cart.js — no section re-render needed here.
   ---------------------------------------------------------- */
function initCartUpdateListener() {}

/* ----------------------------------------------------------
   Gift wrap toggle
   ---------------------------------------------------------- */
function initGiftWrap() {
  const btn = document.getElementById('aneka-gift-wrap-btn');
  if (!btn) return;

  btn.addEventListener('click', async () => {
    if (btn.dataset.unconfigured === 'true') return;

    const variantId = btn.dataset.variantId;
    const inCart    = btn.dataset.inCart === 'true';

    btn.disabled    = true;
    btn.textContent = 'Please wait…';

    try {
      if (inCart) {
        const cartData = await fetch('/cart.js').then(r => r.json());
        const item = cartData.items.find(i => String(i.variant_id) === String(variantId));
        if (item) {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: item.key, quantity: 0 }),
          });
        }
      } else {
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 1 }),
        });
      }
      window.location.reload();
    } catch (_) {
      btn.disabled    = false;
      btn.textContent = inCart ? 'REMOVE GIFT WRAP' : 'ADD A GIFT WRAP';
    }
  });
}

/* ----------------------------------------------------------
   Recommendation add-to-cart buttons
   Uses Shopify's sections parameter on /cart/add.js so we get
   re-rendered cart HTML back in one round-trip — no page reload.
   ---------------------------------------------------------- */
function initRecoButtons() {
  document.querySelectorAll('.aneka-reco-add-btn').forEach(btn => {
    // Guard against double-binding when initRecoButtons is called
    // multiple times on the same elements (e.g. before a card swap)
    if (btn.dataset.anekaInit) return;
    btn.dataset.anekaInit = '1';

    btn.addEventListener('click', async () => {
      const vid = btn.dataset.variantId;
      if (!vid) return;

      btn.disabled    = true;
      btn.textContent = '…';

      const sectionIds = getCartSectionIds();

      try {
        const resp = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: vid, quantity: 1, sections: sectionIds }),
        });
        const data = await resp.json();

        if (data.sections) {
          // Re-render cart item list, footer totals, icon badge
          applyCartSections(data.sections);
        }

        // Recompute the tier tracker (bar + message + recommendations)
        // straight from /cart.js now that the item was added
        if (window.AnekaTierTracker) await window.AnekaTierTracker.refresh();
      } catch (_) {
        btn.disabled    = false;
        btn.textContent = '+ Add';
      }
    });
  });
}

/* ----------------------------------------------------------
   Recommendation carousel — prev/next arrow navigation
   Shows 3 items at a time, slides by 1 on each arrow click,
   wraps around infinitely via modulo arithmetic.
   ---------------------------------------------------------- */
function initRecoCarousel() {
  const carousel = document.querySelector('.aneka-reco-carousel');
  if (!carousel) return;

  const itemsEl = carousel.querySelector('.aneka-reco-items');
  const prevBtn = carousel.querySelector('.aneka-reco-prev');
  const nextBtn = carousel.querySelector('.aneka-reco-next');
  if (!itemsEl || !prevBtn || !nextBtn) return;

  const allItems = Array.from(itemsEl.querySelectorAll('.aneka-reco-item'));
  /* 1 item on mobile (<750px), 3 on desktop — matches CSS flex basis */
  const VISIBLE  = window.innerWidth < 750 ? 1 : 3;

  if (allItems.length <= VISIBLE) {
    prevBtn.style.display = 'none';
    nextBtn.style.display = 'none';
    return;
  }

  const maxIdx = allItems.length - VISIBLE;
  let idx  = 0;
  let busy = false;

  function step() {
    /* Read live item width each time so it works after resize */
    return allItems[0].offsetWidth + 8;
  }

  function moveTo(newIdx, animate) {
    if (busy) return;
    idx = ((newIdx % (maxIdx + 1)) + (maxIdx + 1)) % (maxIdx + 1);

    if (!animate) {
      itemsEl.style.transition = 'none';
      itemsEl.style.transform  = `translateX(-${idx * step()}px)`;
      itemsEl.offsetWidth; // eslint-disable-line no-unused-expressions
      itemsEl.style.transition = '';
      return;
    }

    busy = true;
    itemsEl.style.transition = 'transform 0.35s cubic-bezier(0.4,0,0.2,1)';
    itemsEl.style.transform  = `translateX(-${idx * step()}px)`;
    itemsEl.addEventListener('transitionend', () => { busy = false; }, { once: true });
  }

  prevBtn.addEventListener('click', () => moveTo(idx - 1, true));
  nextBtn.addEventListener('click', () => moveTo(idx + 1, true));

  moveTo(0, false);
}